import { isEmpty, isNil } from 'lodash';

import { fromDateTime, today, toCalendarDate, todayDateTime } from '../dateutils';
import { InvoiceSetting } from '../types';

import {
  DEFAULT_CREDIT_NOTE_SCHEMA,
  DEFAULT_INVOICE_SCHEMA,
  DEFAULT_PAYMENT_REQUEST_SCHEMA,
  DEFAULT_RECEIPT_SCHEMA,
  DEFAULT_VARIOUS_OPERATION_SCHEMA,
  getNextIncrementNumberForSchema,
  type FiscalYearStart,
  type NextIncrementNumberOptions,
} from './invoiceHelper';

export enum InvoiceFormatTokenType {
  TEXT = 'TEXT',
  YEAR_FULL = 'YEAR_FULL',
  YEAR_SHORT = 'YEAR_SHORT',
  MONTH = 'MONTH',
  SEQUENCE = 'SEQUENCE',
}

export interface InvoiceFormatTextToken {
  type: InvoiceFormatTokenType.TEXT;
  text: string;
}

export interface InvoiceFormatSequenceToken {
  type: InvoiceFormatTokenType.SEQUENCE;
  width: number;
}

export interface InvoiceFormatDateToken {
  type: InvoiceFormatTokenType.YEAR_FULL | InvoiceFormatTokenType.YEAR_SHORT | InvoiceFormatTokenType.MONTH;
}

export type InvoiceFormatToken = InvoiceFormatTextToken | InvoiceFormatSequenceToken | InvoiceFormatDateToken;

export interface InvoiceNumberFormat {
  tokens: InvoiceFormatToken[];
  separator: string;
}

export const INVOICE_FORMAT_SEPARATORS = ['/', '-', '.', '_'];
export const DEFAULT_INVOICE_FORMAT_SEPARATOR = '/';
export const INVOICE_FORMAT_COUNTER_WIDTHS = [3, 4, 5];
export const DEFAULT_INVOICE_FORMAT_COUNTER_WIDTH = 4;

const CALENDAR_DATE_LENGTH = 10;
const YEAR_FULL_SCHEMA_TOKEN = 'yyyy';
const YEAR_SHORT_SCHEMA_TOKEN = 'yy';
const MONTH_SCHEMA_TOKEN = 'MM';
const SEQUENCE_SCHEMA_CHARACTER = 'X';
const TEXT_QUOTE = "'";

export enum InvoiceDocumentType {
  INVOICE = 'INVOICE',
  CREDIT_NOTE = 'CREDIT_NOTE',
  PAYMENT_REQUEST = 'PAYMENT_REQUEST',
  RECEIPT = 'RECEIPT',
  VARIOUS_OPERATION = 'VARIOUS_OPERATION',
}

export const INVOICE_DOCUMENT_TYPES = [
  InvoiceDocumentType.INVOICE,
  InvoiceDocumentType.CREDIT_NOTE,
  InvoiceDocumentType.PAYMENT_REQUEST,
  InvoiceDocumentType.RECEIPT,
  InvoiceDocumentType.VARIOUS_OPERATION,
];

interface InvoiceSettingDocumentFields {
  format: keyof InvoiceSetting;
  incrementNumber: keyof InvoiceSetting;
  lastUpdate: keyof InvoiceSetting;
}

export const INVOICE_SETTING_DOCUMENT_FIELDS = {
  [InvoiceDocumentType.INVOICE]: {
    format: 'invoiceFormat',
    incrementNumber: 'invoiceIncrementNumber',
    lastUpdate: 'invoiceLastUpdate',
  },
  [InvoiceDocumentType.CREDIT_NOTE]: {
    format: 'creditNoteFormat',
    incrementNumber: 'creditNoteIncrementNumber',
    lastUpdate: 'creditNoteLastUpdate',
  },
  [InvoiceDocumentType.PAYMENT_REQUEST]: {
    format: 'paymentRequestFormat',
    incrementNumber: 'paymentRequestIncrementNumber',
    lastUpdate: 'paymentRequestLastUpdate',
  },
  [InvoiceDocumentType.RECEIPT]: {
    format: 'receiptFormat',
    incrementNumber: 'receiptIncrementNumber',
    lastUpdate: 'receiptLastUpdate',
  },
  [InvoiceDocumentType.VARIOUS_OPERATION]: {
    format: 'variousOperationFormat',
    incrementNumber: 'variousOperationIncrementNumber',
    lastUpdate: 'variousOperationLastUpdate',
  },
} as const satisfies Record<InvoiceDocumentType, InvoiceSettingDocumentFields>;

export const DEFAULT_SCHEMA_BY_INVOICE_DOCUMENT_TYPE: Record<InvoiceDocumentType, string> = {
  [InvoiceDocumentType.INVOICE]: DEFAULT_INVOICE_SCHEMA,
  [InvoiceDocumentType.CREDIT_NOTE]: DEFAULT_CREDIT_NOTE_SCHEMA,
  [InvoiceDocumentType.PAYMENT_REQUEST]: DEFAULT_PAYMENT_REQUEST_SCHEMA,
  [InvoiceDocumentType.RECEIPT]: DEFAULT_RECEIPT_SCHEMA,
  [InvoiceDocumentType.VARIOUS_OPERATION]: DEFAULT_VARIOUS_OPERATION_SCHEMA,
};

const toSchemaPart = (token: InvoiceFormatToken): string => {
  switch (token.type) {
    case InvoiceFormatTokenType.TEXT:
      return `${TEXT_QUOTE}${token.text}${TEXT_QUOTE}`;
    case InvoiceFormatTokenType.SEQUENCE:
      return SEQUENCE_SCHEMA_CHARACTER.repeat(token.width);
    case InvoiceFormatTokenType.YEAR_FULL:
      return YEAR_FULL_SCHEMA_TOKEN;
    case InvoiceFormatTokenType.YEAR_SHORT:
      return YEAR_SHORT_SCHEMA_TOKEN;
    case InvoiceFormatTokenType.MONTH:
      return MONTH_SCHEMA_TOKEN;
    default:
      return '';
  }
};

const toPatternPart = (token: InvoiceFormatToken): string =>
  token.type === InvoiceFormatTokenType.TEXT ? token.text : toSchemaPart(token);

export const parseInvoiceNumberFormat = (schema: string): InvoiceNumberFormat => {
  const tokens: InvoiceFormatToken[] = [];
  let separator: string | null = null;
  let position = 0;

  while (position < schema.length) {
    if (schema[position] === TEXT_QUOTE) {
      const endQuoteIndex = schema.indexOf(TEXT_QUOTE, position + 1);
      const textEnd = endQuoteIndex === -1 ? schema.length : endQuoteIndex;
      tokens.push({ type: InvoiceFormatTokenType.TEXT, text: schema.substring(position + 1, textEnd) });
      position = endQuoteIndex === -1 ? schema.length : endQuoteIndex + 1;
      continue;
    }
    if (schema.startsWith(YEAR_FULL_SCHEMA_TOKEN, position)) {
      tokens.push({ type: InvoiceFormatTokenType.YEAR_FULL });
      position += YEAR_FULL_SCHEMA_TOKEN.length;
      continue;
    }
    if (schema.startsWith(YEAR_SHORT_SCHEMA_TOKEN, position)) {
      tokens.push({ type: InvoiceFormatTokenType.YEAR_SHORT });
      position += YEAR_SHORT_SCHEMA_TOKEN.length;
      continue;
    }
    if (schema.startsWith(MONTH_SCHEMA_TOKEN, position)) {
      tokens.push({ type: InvoiceFormatTokenType.MONTH });
      position += MONTH_SCHEMA_TOKEN.length;
      continue;
    }
    if (schema[position] === SEQUENCE_SCHEMA_CHARACTER) {
      let width = 0;
      while (schema[position + width] === SEQUENCE_SCHEMA_CHARACTER) {
        width += 1;
      }
      tokens.push({ type: InvoiceFormatTokenType.SEQUENCE, width });
      position += width;
      continue;
    }
    if (separator === null) {
      separator = schema[position];
    }
    position += 1;
  }

  return { tokens, separator: separator ?? DEFAULT_INVOICE_FORMAT_SEPARATOR };
};

export const buildInvoiceNumberSchema = ({ tokens, separator }: InvoiceNumberFormat): string =>
  tokens.map(toSchemaPart).join(separator);

export const getInvoiceNumberFormatPattern = ({ tokens, separator }: InvoiceNumberFormat): string =>
  tokens.map(toPatternPart).join(separator);

export const getInvoiceFormatCounterWidth = (format: InvoiceNumberFormat): number | null => {
  const sequenceToken = format.tokens.find((token) => token.type === InvoiceFormatTokenType.SEQUENCE);
  return sequenceToken?.type === InvoiceFormatTokenType.SEQUENCE ? sequenceToken.width : null;
};

export enum InvoiceNumberFormatError {
  NO_TOKEN = 'noToken',
  NO_SEQUENCE = 'noSequence',
  EMPTY_TEXT = 'emptyText',
  MULTIPLE_SEQUENCES = 'multipleSequences',
}

export enum InvoiceNumberFormatWarning {
  MONTH_WITHOUT_YEAR = 'monthWithoutYear',
  MID_MONTH_FISCAL_YEAR = 'midMonthFiscalYear',
}

const FIRST_DAY_OF_MONTH = 1;

export const getInvoiceNumberFormatWarnings = (
  format: InvoiceNumberFormat,
  fiscalYearStart?: FiscalYearStart
): InvoiceNumberFormatWarning[] => {
  const hasYear = format.tokens.some(
    (token) => token.type === InvoiceFormatTokenType.YEAR_FULL || token.type === InvoiceFormatTokenType.YEAR_SHORT
  );
  const hasMonth = format.tokens.some((token) => token.type === InvoiceFormatTokenType.MONTH);
  const warnings: InvoiceNumberFormatWarning[] = [];
  if (hasMonth && !hasYear) {
    warnings.push(InvoiceNumberFormatWarning.MONTH_WITHOUT_YEAR);
  }
  if (hasMonth && !isNil(fiscalYearStart) && fiscalYearStart.day !== FIRST_DAY_OF_MONTH) {
    warnings.push(InvoiceNumberFormatWarning.MID_MONTH_FISCAL_YEAR);
  }
  return warnings;
};

export const getInvoiceNumberFormatErrors = ({ tokens }: InvoiceNumberFormat): InvoiceNumberFormatError[] => {
  const errors: InvoiceNumberFormatError[] = [];
  const sequenceCount = tokens.filter((token) => token.type === InvoiceFormatTokenType.SEQUENCE).length;
  if (isEmpty(tokens)) {
    errors.push(InvoiceNumberFormatError.NO_TOKEN);
  }
  if (sequenceCount === 0) {
    errors.push(InvoiceNumberFormatError.NO_SEQUENCE);
  }
  if (sequenceCount > 1) {
    errors.push(InvoiceNumberFormatError.MULTIPLE_SEQUENCES);
  }
  if (tokens.some((token) => token.type === InvoiceFormatTokenType.TEXT && token.text.trim() === '')) {
    errors.push(InvoiceNumberFormatError.EMPTY_TEXT);
  }
  return errors;
};

export interface InvoiceDocumentSequence {
  schema: string;
  incrementNumber: number;
  lastUpdate: string;
}

// Per-field fallback: rows predating migrations 12 and 54 can miss the receipt/various-operation columns.
export const getInvoiceDocumentSequence = (
  invoiceSetting: InvoiceSetting | undefined,
  documentType: InvoiceDocumentType
): InvoiceDocumentSequence => {
  const fields = INVOICE_SETTING_DOCUMENT_FIELDS[documentType];
  return {
    schema: invoiceSetting?.[fields.format] ?? DEFAULT_SCHEMA_BY_INVOICE_DOCUMENT_TYPE[documentType],
    incrementNumber: invoiceSetting?.[fields.incrementNumber] ?? 0,
    lastUpdate: invoiceSetting?.[fields.lastUpdate] ?? fromDateTime(todayDateTime()),
  };
};

// `*LastUpdate` has historically held plain AWSDate values, so only its date part is read.
export const getNextInvoiceNumberForSetting = (
  schema: string,
  incrementNumber: number,
  lastUpdate: string,
  { fiscalYearStart, date = today() }: NextIncrementNumberOptions = {}
): number =>
  getNextIncrementNumberForSchema(incrementNumber, toCalendarDate(lastUpdate.slice(0, CALENDAR_DATE_LENGTH)), schema, {
    fiscalYearStart,
    date,
  });

/**
 * Copies the structure of one format onto another, keeping the target's own literal text so that
 * credit notes stay CN and receipts stay RCPT when "apply to all" is used.
 */
export const applyInvoiceFormatStructure = (
  source: InvoiceNumberFormat,
  target: InvoiceNumberFormat
): InvoiceNumberFormat => {
  const targetTextToken = target.tokens.find((token) => token.type === InvoiceFormatTokenType.TEXT);
  const targetText = targetTextToken?.type === InvoiceFormatTokenType.TEXT ? targetTextToken.text : '';
  return {
    separator: source.separator,
    tokens: source.tokens.map((token) =>
      token.type === InvoiceFormatTokenType.TEXT
        ? { type: InvoiceFormatTokenType.TEXT, text: targetText }
        : { ...token }
    ),
  };
};
